import React, { PureComponent } from 'react';
import PropType from 'prop-types';
import { Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import amplitude from 'amplitude-js/amplitude';
import {
  PAGE_VISIT_PASSWORD_RESET, PASSWORD_RESET_USED, PASSWORD_RESET_SUCCESS, PASSWORD_RESET_ERROR,
} from '../../constants/amplitudeEvents';
import ErrorMessage from '../../components/Errors/ErrorMessage';
import Loading from '../../components/Loading/Loading';
import getUrlParameters from '../../utils/getUrlParameters';
import apiService from '../../utils/api/apiService';

export class PasswordResetPage extends PureComponent {
  static propTypes = {
    location: PropType.object.isRequired,
    t: PropType.func.isRequired,
  }

  static defaultProps = {
    location: null,
    t: (str) => str,
  }

  state = {
    userIdentifier: '',
    password: '',
    passwordRepeated: '',
    resetToken: getUrlParameters(this.props.location.search).pt,
    resetRequestSent: false,
    passwordsMatch: true,
    passwordResetSuccesfully: false,
    passwordResetError: false,
    resetRequestError: false,
    passwordTooShort: false,
    loading: false,
  }

  componentDidMount() {
    amplitude.getInstance().logEvent(PAGE_VISIT_PASSWORD_RESET, {
      EmailLinkFollowed: Boolean(getUrlParameters(this.props.location.search).pt),
    });
  }

  setUserIdentifier = (value) => this.setState(() => ({ userIdentifier: value }))

  setPassword = (value, isRepeat = false) => {
    this.setState((prevState) => ({
      password: isRepeat ? prevState.password : value,
      passwordRepeated: isRepeat ? value : prevState.passwordRepeated,
      passwordsMatch: prevState.passwordsMatch || (isRepeat ? prevState.password === value : value === prevState.passwordRepeated),
      passwordTooShort: prevState.passwordTooShort && value.length < 8,
    }));
  }

  checkIfPasswordsMatch = () => {
    if (this.state.password !== '' && this.state.passwordRepeated !== '' && this.state.password !== this.state.passwordRepeated) {
      this.setState(() => ({ passwordsMatch: false }));
    }
  }

  requestResetTokenEmail = () => {
    this.setState(() => ({ loading: true }));
    let params = { username: this.state.userIdentifier };
    if (__BRAND__.name === 'suunto') params = { ...params, brand: 'suuntoapp' };
    return apiService.get('resetPassword', params)
      .then(() => {
        amplitude.getInstance().logEvent(PASSWORD_RESET_USED);
        this.setState(() => ({ resetRequestSent: true, loading: false }));
      })
      .catch(() => this.setState(() => ({ resetRequestError: true, loading: false })));
  }

  resetPassword = () => {
    if (this.state.password !== this.state.passwordRepeated) return this.setState(() => ({ passwordsMatch: false }));
    if (this.state.password.length < 8) return this.setState(() => ({ passwordTooShort: true }));
    const data = new FormData();
    data.append('new', this.state.password);
    data.append('current', getUrlParameters(this.props.location.search).pt);
    this.setState(() => ({ loading: true }));
    return apiService.post('changePassword', data)
      .then((res) => {
        if (res.payload) {
          amplitude.getInstance().logEvent(PASSWORD_RESET_SUCCESS);
          return this.setState(() => ({ passwordResetSuccesfully: true, loading: false }));
        }
        amplitude.getInstance().logEvent(PASSWORD_RESET_ERROR, { ErrorReason: 'Server denied change' });
        return this.setState(() => ({ passwordResetError: true, loading: false }));
      })
      .catch((err) => {
        amplitude.getInstance().logEvent(PASSWORD_RESET_ERROR, { ErrorReason: err.toString() });
        this.setState(() => ({ passwordResetError: true, loading: false }));
      });
  }

  render() {
    const {
      userIdentifier,
      password,
      passwordRepeated,
      resetToken,
      resetRequestSent,
      passwordResetSuccesfully,
      passwordsMatch,
      passwordResetError,
      passwordTooShort,
      resetRequestError,
      loading,
    } = this.state;
    const { t } = this.props;
    return (
      <section className="section">
        <div className="columns is-centered">
          <div className="column is-4-desktop is-8-tablet">
            <div className="box is-radiusless is-borderless is-relative">
              <div className="has-padding-large is-paddingless-mobile">
                {/* START RESETTING */}
                {!resetToken && !resetRequestSent
              && (
              <form data-testid="requestResetTokenForm" onSubmit={(e) => { e.preventDefault(); this.requestResetTokenEmail(); }}>
                <h1 className="title is-3 is-uppercase is-spaced">{t('RESET_PASSWORD')}</h1>
                <p className="subtitle is-5">{t('RESET_PASSWORD_INSTRUCTIONS')}</p>
                {resetRequestError && <ErrorMessage testId="resetRequestServerError" text={t('ERROR_OCCURED_PLEASE_TRY_LATER')} />}
                <div className="field">
                  <input className="input is-primary is-medium" type="text" value={userIdentifier} placeholder={t('EMAIL_OR_USERNAME')} onChange={(e) => this.setUserIdentifier(e.target.value)} />
                </div>
                <button className="button is-primary is-medium is-fullwidth is-uppercase" type="submit" disabled={userIdentifier === ''}>{t('SEND_RESET_EMAIL')}</button>
              </form>
              )}

                {/* EMAIL BEEN SENT */}
                {!resetToken && resetRequestSent
              && (
              <div data-testid="requestResetTokenDone" className="">
                <h1 className="title is-3 is-uppercase is-spaced">{t('PASSWORD_RESET_LINK_SENT')}</h1>
                <p className="subtitle is-5">{t('PASSWORD_RESET_EMAIL_SENT')}</p>
                <Link to="/" className="button is-primary is-medium is-fullwidth is-uppercase">{t('BACK_TO_HOME')}</Link>
              </div>
              )}

                {/* CHANGE PASSWORD */}
                {resetToken && !passwordResetSuccesfully
              && (
              <form data-testid="resetPasswordForm" onSubmit={(e) => { e.preventDefault(); this.resetPassword(); }}>
                <h1 className="title is-3 is-uppercase is-spaced">{t('CHOOSE_A_NEW_PASSWORD')}</h1>
                <p className="subtitle is-5">{t('CHOOSE_NEW_PASSWORD')}</p>
                {passwordResetError && <ErrorMessage testId="resetPasswordServerError" text={t('ERROR_OCCURED_PLEASE_TRY_LATER')} />}
                {passwordTooShort && <ErrorMessage testId="passwordStrengthError" text={t('PASSWORD_MIN_8_CHARACTERS')} />}
                {!passwordsMatch && <ErrorMessage testId="passwordMismatch" text={t('PASSWORDS_DONT_MATCH')} />}
                <div className="field">
                  <input
                    className="input is-primary is-medium"
                    type="password"
                    value={password}
                    placeholder={t('PASSWORD')}
                    onChange={(e) => this.setPassword(e.target.value, false)}
                    onBlur={this.checkIfPasswordsMatch}
                  />
                </div>
                <div className="field">
                  <input
                    className="input is-primary is-medium"
                    type="password"
                    value={passwordRepeated}
                    placeholder={t('RE_ENTER_PASSWORD')}
                    onChange={(e) => this.setPassword(e.target.value, true)}
                    onBlur={this.checkIfPasswordsMatch}
                  />
                </div>
                <button className="button is-primary is-medium is-fullwidth is-uppercase" type="submit" disabled={!passwordsMatch || password.length === 0 || passwordRepeated.length === 0}>{t('RESET_PASSWORD')}</button>
              </form>
              )}

                {/* SUCCESS */}
                {resetToken && passwordResetSuccesfully
              && (
              <div data-testid="resetPasswordDone">
                <h1 className="title is-3 is-uppercase is-spaced">{t('PASSWORD_CHANGED')}</h1>
                {/* <p className="subtitle is-5">{t('CONTINUE_TO_SITE')}</p> */}
                {/* <Link to="/login" className="button is-primary is-medium is-fullwidth is-uppercase">{t('LOGIN')}</Link> */}
              </div>
              )}

                {loading && <Loading />}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
}

export default translate()(PasswordResetPage);
